import React, { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { GridColDef } from '@mui/x-data-grid-pro';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@mui/material/Button';
import { LG_WIDTH, SM_WIDTH, STAFF_CACHE_KEY } from '../../constants';
import BuildColumns from './helpers/Columns';
import GenericDataGridAndSideForm from '../../components/datagridComponents/GenericDataGridAndSideForm';
import CreateEditStaffCard from './components/CreateEditStaffCard';
import useStaffRolesOptions from '../../helperHooks/useStaffRolesOptions';
import { PageMetadata } from '../../components/datagridComponents/paginationHelpers';
import useLocations from '../../helperHooks/useLocations';
import { MobileRowMainContent } from './components/MobileRowStructure';
import { FilterContextProvider } from '../../context/FilterContext';
import useFiltersFetchedData from '../../helperHooks/useFiltersFetchedData';
import PageHeader from '../../components/pageHeaderComponents/PageHeader';
import ActionsContainer from '../../components/ActionsContainer';
import { StepContext } from '../../context/StepContext';
import { API_V1_HOSTNAME_KEY } from '../../localStorageKeys';
import usePageTitle from '../../helperHooks/usePageTitle';

export type Staff = {
  id: number;
  name: string;
  hourlyRate: number;
  staffRoleId: number;
  passcode: string;
  showAtAllLocations: boolean;
  mainLocationId: number;
  jobCodeIds: number[];
  ageVerificationGroupId?: number | null;
};

export type StaffErrors = {
  name?: string;
  mainLocationId?: string;
  showAtAllLocations?: string;
  hourlyRate?: string;
  staffRoleId?: string;
  passcode?: string;
  jobCodeIds?: string;
  ageVerificationGroupId?: string;
};

const StaffListContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const hideExtraColumns = useMediaQuery(theme.breakpoints.between(SM_WIDTH, LG_WIDTH));
  const { locations } = useLocations();
  const { staffRolesOptions: staffRoles, isLoadingStaffRolesOptions } = useStaffRolesOptions();
  const { goToNextStep } = useContext(StepContext);

  usePageTitle('nav.staff');

  const { fetchData, data, isLoading, isError, error, isRefetching } = useFiltersFetchedData<{
    data: Staff[];
    metadata: PageMetadata;
  }>(
    STAFF_CACHE_KEY,
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/staff`,
    'staff list'
  );

  const buildColumns = useCallback(
    (): GridColDef[] => BuildColumns(staffRoles, locations),
    [staffRoles, locations]
  );

  const columnVisibility = useMemo(
    () => ({
      name: true,
      mainLocationId: true,
      showAtAllLocations: !hideExtraColumns,
      staffRoleId: true,
      passcode: !hideExtraColumns,
    }),
    [hideExtraColumns]
  );

  const unauthorized = (error as any)?.code === 401;

  return (
    <>
      <PageHeader omitTextHeader />
      <GenericDataGridAndSideForm<Staff, StaffErrors>
        data={data?.data}
        metadata={data?.metadata}
        isLoading={isLoading || isRefetching || isLoadingStaffRolesOptions}
        isError={isError}
        unauthorized={unauthorized}
        fetchData={fetchData}
        BuildColumns={buildColumns}
        CreateComponent={CreateEditStaffCard}
        translationKey="staff"
        endpoint={`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/staff`}
        sentryMsg="staff"
        cacheKey={STAFF_CACHE_KEY}
        pageDescription={t('screens.staff.staffListScreenTooltip')}
        mobileRowMainContent={MobileRowMainContent}
        columnVisibility={columnVisibility}
      />
      <ActionsContainer>
        <Button
          variant="outlined"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={() => {
            navigate('/staff-roles');
          }}
        >
          {t('actions.previous')}
        </Button>
        <Button
          variant="contained"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={goToNextStep}
        >
          {t('actions.next')}
        </Button>
      </ActionsContainer>
    </>
  );
};

const StaffList = () => (
  <FilterContextProvider>
    <StaffListContent />
  </FilterContextProvider>
);
export default StaffList;
